import { readStringClaim } from "@/lib/auth/claims";

export type DatadogAuthUserSnapshot = {
  id?: string | null;
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
  identities?: Array<{
    identity_data?: Record<string, unknown> | null;
  } | null> | null;
};

export type DatadogUserIdentity = {
  id: string;
  email?: string;
  name?: string;
  orgId: string | null;
};

const NAME_KEYS = ["full_name", "name", "display_name"] as const;

function readTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readRecord(record: unknown, key: string): Record<string, unknown> | null {
  if (typeof record !== "object" || record === null) return null;
  const value = (record as Record<string, unknown>)[key];
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readRecordString(record: unknown, key: string): string | null {
  if (typeof record !== "object" || record === null) return null;
  return readTrimmedString((record as Record<string, unknown>)[key]);
}

function readNameFromRecord(record: unknown): string | null {
  if (typeof record !== "object" || record === null) return null;

  for (const key of NAME_KEYS) {
    const value = readRecordString(record, key);
    if (value) return value;
  }

  const given = readRecordString(record, "given_name") ?? readRecordString(record, "givenName");
  const family = readRecordString(record, "family_name") ?? readRecordString(record, "familyName");
  const composed = [given, family].filter(Boolean).join(" ");
  return composed.length > 0 ? composed : null;
}

function readNameFromIdentities(user: DatadogAuthUserSnapshot | null | undefined): string | null {
  for (const identity of user?.identities ?? []) {
    const name = readNameFromRecord(identity?.identity_data);
    if (name) return name;
  }
  return null;
}

export function toDatadogRumUser(identity: DatadogUserIdentity): {
  id: string;
  email?: string;
  name?: string;
} {
  return {
    id: identity.id,
    ...(identity.email ? { email: identity.email } : {}),
    ...(identity.name ? { name: identity.name } : {}),
  };
}

export function resolveDatadogUserIdentity({
  user,
  claims,
}: {
  user: DatadogAuthUserSnapshot | null | undefined;
  claims: unknown;
}): DatadogUserIdentity | null {
  const id = readTrimmedString(user?.id) ?? readStringClaim(claims, "sub");
  if (!id) return null;

  const claimsMetadata = readRecord(claims, "user_metadata");
  const email =
    readTrimmedString(user?.email) ??
    readRecordString(user?.user_metadata, "email") ??
    readStringClaim(claims, "email") ??
    readRecordString(claimsMetadata, "email");
  const name =
    readNameFromRecord(user?.user_metadata) ??
    readNameFromRecord(claims) ??
    readNameFromRecord(claimsMetadata) ??
    readNameFromIdentities(user);

  return {
    id,
    ...(email ? { email } : {}),
    ...(name ? { name } : {}),
    orgId: readStringClaim(claims, "org_id"),
  };
}
